/**
 * Accesos → Ámbitos, the list (Plan J, J3 — mockup 3, list half).
 *
 * An *ámbito* is the cut of the data a person reads: a set of AND-ed
 * rules over dimension keys, materialised as one DuckDB file per (scope,
 * project). This screen replaces the SQL an operator runs today
 * (operator-runbook §6, *"**SQL a mano** (J3 trae el CRUD)"*).
 *
 * THE NUMBER THIS LIST EXISTS TO MAKE LOUD
 * ----------------------------------------
 * `applies_to_count == 0`. A scope applicable to no project grants
 * nothing, anywhere, and it looks completely healthy in `SELECT * FROM
 * scopes`. So the column never renders a bare number: it says *"n con
 * acceso · m sin acceso"*, and zero is a danger chip with the reason in
 * its tooltip.
 *
 * WHAT IS NOT OFFERED, AND WHY
 * ----------------------------
 * * The reserved `all` row and the derived `eff_…` rows are LISTED —
 *   they are real rows people are bound to, and a list that hid them
 *   would make "who is on which scope" unanswerable from the screen that
 *   is supposed to answer it — but their row actions are hidden through
 *   `RowAction.hidden`, driven by the backend's own `editable` flag.
 *   `build_scopes_for_project` skips `all` by design, so offering
 *   Reconstruir on it would offer an action that logs a debug line and
 *   does nothing.
 * * No `estimated_rows` here. It costs one DuckDB reader and one scan
 *   PER PROJECT PER SCOPE; it is opt-in on the detail view.
 * * No inline rules editing. Editing rules moves nothing on disk (see
 *   `ScopeDetailPage`), so the consequence has to be shown, and that
 *   does not fit in a row.
 */

import { Button } from "@omnitok/ui";
import {
  Callout,
  DataTable,
  DateCell,
  EmptyState,
  PageContainer,
  PageHeader,
  Section,
  StatusChip,
  type Column,
  type RowAction,
  type StatusDef,
} from "@pervasivemind/admin-ui";
import { Spinner } from "@pervasivemind/bi-components";
import { Eye, Hammer, Plus, ShieldCheck, Trash2 } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useT } from "@/i18n";
import {
  SCOPE_ALL,
  scopesApi,
  type ScopeSummaryOut,
} from "@/lib/access-api";
import {
  AppliesToCell,
  DictChip,
  ScopeIdentityCell,
} from "@/pages/admin-access/AccessCells";
import { projectsApi } from "@/lib/builder-api";
import { AccessTabs } from "@/pages/admin-access/AccessTabs";
import { scopeStateDict, type TFn } from "@/pages/admin-access/access-status";
import { ScopeCreateModal } from "@/pages/admin-access/ScopeCreateModal";
import { ScopeDeleteModal } from "@/pages/admin-access/ScopeDeleteModal";

/**
 * The worst of a scope's per-project states, as ONE word.
 *
 * Order matters and it is not the state dictionary's: a scope with one
 * failure and nine successes is a scope with a failure. `never_built`
 * outranks `not_applicable` because the first costs somebody a 503 and
 * the second is a correct configuration.
 *
 * Exported so a test can walk it without a DOM.
 */
export function worstScopeState(row: ScopeSummaryOut): string | null {
  // A `null` counter is UNKNOWN, and `(x ?? 0) > 0` would read it as
  // "none of those" — a green rollup over numbers nobody computed. Any
  // unknown makes the whole verdict unknown, which the cell renders as
  // "sin calcular" rather than as a state.
  if (
    row.applies_to_count === null ||
    row.failed_count === null ||
    row.never_built_count === null ||
    row.built_count === null
  ) {
    return null;
  }
  if (row.failed_count > 0) return "failed";
  if (row.never_built_count > 0) return "never_built";
  if (row.applies_to_count === 0) return "not_applicable";
  if (row.built_count > 0) return "built";
  return null;
}

export function AccessScopesPage(): JSX.Element {
  const { t, locale } = useT();
  const navigate = useNavigate();

  const [rows, setRows] = useState<ScopeSummaryOut[] | null>(null);
  const [projectTotal, setProjectTotal] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState<ScopeSummaryOut | null>(null);

  const refresh = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      // The scope list carries no project TOTAL, and the widest
      // `applies_to_count` is only a lower bound on it — using that as
      // the denominator would print "0 sin acceso" for the widest scope
      // whether or not it is true. So the real count is fetched, and
      // when it cannot be, the cell says "not computed" rather than
      // inventing a denominator.
      const [out, projects] = await Promise.all([
        scopesApi.list(),
        projectsApi.list(),
      ]);
      setRows(out.scopes);
      setProjectTotal(projects.projects.length);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const states = useMemo(() => scopeStateDict(t), [t]);

  const columns: Column<ScopeSummaryOut>[] = [
    {
      id: "scope",
      header: t("access.scopes.col.scope"),
      cell: (row) => <ScopeIdentityCell id={row.id} name={row.name} />,
    },
    {
      id: "rules",
      header: t("access.scopes.col.rules"),
      cell: (row) =>
        row.rules.length === 0 ? (
          <span
            className="text-[13px] text-neutral-500"
            title={
              row.id === SCOPE_ALL
                ? t("access.scopes.rules.allHint")
                : t("access.scopes.rules.noneHint")
            }
          >
            {row.id === SCOPE_ALL
              ? t("access.scopes.rules.all")
              : t("access.scopes.rules.none")}
          </span>
        ) : (
          <div className="flex flex-wrap gap-1">
            {row.rules.map((rule) => (
              <StatusChip
                key={rule.dimension_key}
                tone="neutral"
                label={t("access.scopes.rules.chip", {
                  key: rule.dimension_key,
                  n: rule.values.length,
                })}
                title={rule.values.map((v) => String(v)).join(", ")}
              />
            ))}
          </div>
        ),
    },
    {
      id: "projects",
      header: t("access.scopes.col.projects"),
      width: "w-52",
      cell: (row) => (
        <AppliesToCell
          appliesTo={row.applies_to_count}
          total={projectTotal}
          t={t}
        />
      ),
    },
    {
      id: "used",
      header: t("access.scopes.col.usedBy"),
      width: "w-48",
      cell: (row) =>
        row.usage.groups.length === 0 ? (
          <span
            className="text-[13px] text-neutral-500"
            title={t("access.scopes.usedByNoneHint")}
          >
            {t("access.scopes.usedByNone")}
          </span>
        ) : (
          <span
            className="text-[13px] text-neutral-700"
            title={row.usage.groups.map((g) => g.name).join(", ")}
          >
            {t("access.scopes.usedBy", {
              n: row.usage.groups.length,
              people: row.usage.member_user_count,
            })}
          </span>
        ),
    },
    {
      id: "state",
      header: t("access.scopes.col.state"),
      width: "w-44",
      cell: (row) => <WorstStateCell row={row} states={states} t={t} />,
    },
    {
      id: "updated",
      header: t("access.scopes.col.updated"),
      width: "w-40",
      cell: (row) => (
        <DateCell
          value={row.updated_at}
          locale={locale}
          mode="absolute"
          invalidHint={t("access.common.badDate")}
        />
      ),
    },
  ];

  const actions: RowAction<ScopeSummaryOut>[] = [
    {
      id: "detail",
      label: t("access.scopes.actions.detail"),
      icon: <Eye size={14} />,
      onSelect: (row) =>
        navigate(`/admin/access/scopes/${encodeURIComponent(row.id)}`),
    },
    {
      id: "rebuild",
      label: t("access.scopes.actions.rebuild"),
      icon: <Hammer size={14} />,
      // Hidden, never disabled: `all` has no file to build and a derived
      // scope is owned by the recompute. A disabled control would tell
      // the operator "this is possible, just not now", which is false.
      hidden: (row) => !row.editable,
      onSelect: (row) =>
        navigate(`/admin/access/scopes/${encodeURIComponent(row.id)}`),
    },
    {
      id: "delete",
      label: t("access.scopes.actions.delete"),
      icon: <Trash2 size={14} />,
      danger: true,
      hidden: (row) => !row.editable,
      onSelect: (row) => setDeleting(row),
    },
  ];

  return (
    <PageContainer wide>
      <PageHeader
        title={t("access.scopes.title")}
        subtitle={t("access.scopes.subtitle")}
        actions={
          <Button
            variant="primary"
            size="sm"
            leftIcon={<Plus size={14} />}
            onClick={() => setCreating(true)}
          >
            {t("access.scopes.add")}
          </Button>
        }
      />
      <AccessTabs active="scopes" />

      {error !== null && (
        <Callout tone="danger" title={t("access.common.errorTitle")}>
          <span className="whitespace-pre-wrap">{error}</span>
        </Callout>
      )}

      <Section flush>
        {loading ? (
          <div className="flex items-center justify-center gap-2 px-5 py-10 text-sm text-neutral-500">
            <Spinner size={16} ariaLabel={t("common.loading")} />
            {t("common.loading")}
          </div>
        ) : rows === null ? null : (
          <DataTable
            columns={columns}
            rows={rows}
            rowKey={(row) => row.id}
            actions={actions}
            caption={t("access.scopes.title")}
            onRowClick={(row) =>
              navigate(`/admin/access/scopes/${encodeURIComponent(row.id)}`)
            }
            empty={
              <EmptyState
                icon={<ShieldCheck size={24} />}
                title={t("access.scopes.empty.title")}
                description={t("access.scopes.empty.desc")}
                action={
                  <Button
                    variant="primary"
                    size="sm"
                    leftIcon={<Plus size={14} />}
                    onClick={() => setCreating(true)}
                  >
                    {t("access.scopes.add")}
                  </Button>
                }
              />
            }
          />
        )}
      </Section>

      <ScopeCreateModal
        open={creating}
        onClose={() => setCreating(false)}
        onCreated={(id) => {
          setCreating(false);
          navigate(`/admin/access/scopes/${encodeURIComponent(id)}`);
        }}
      />

      <ScopeDeleteModal
        scope={deleting}
        onClose={() => setDeleting(null)}
        onDeleted={() => {
          setDeleting(null);
          void refresh();
        }}
      />
    </PageContainer>
  );
}

/**
 * The worst-of rollup, as a component.
 *
 * A component and not an inline arrow in `columns` for the reason J2
 * measured: while the cell is inline, a test can assert everything about
 * the dictionary and nothing about the screen, and swapping
 * `resolveStatus` for a prettified raw key leaves every test green.
 */
export function WorstStateCell({
  row,
  states,
  t,
}: {
  row: ScopeSummaryOut;
  states: Record<string, StatusDef>;
  t: TFn;
}): JSX.Element {
  const worst = worstScopeState(row);
  if (worst === null) {
    return (
      <span
        className="text-[13px] text-neutral-500"
        title={t("access.common.notComputedHint")}
      >
        {t("access.common.notComputed")}
      </span>
    );
  }
  return <DictChip value={worst} dictionary={states} t={t} />;
}
